import { api } from '../../api'
import type {
  RestaurantListDTO,
  UpdateRestaurantDTO,
  ChangePlanRequestDTO,
  RestaurantStatus,
  SubscriptionPlan,
} from '@/types/admin'

export interface RestaurantDetailDTO {
  id: number
  name: string
  email: string
  phone?: string
  currentPlan: SubscriptionPlan
  status: RestaurantStatus
  commissionPercentage: number
  totalOrdersLast30Days: number
  monthlyRecurringRevenue: number
  createdAt: string
  updatedAt: string
}

export interface RestaurantsPageResponse {
  content: RestaurantListDTO[]
  totalElements: number
  totalPages: number
  size: number
  number: number
}

/**
 * Get paginated list of restaurants with optional filters
 */
export async function getRestaurants(params: {
  page?: number
  size?: number
  status?: RestaurantStatus
  plan?: SubscriptionPlan
  search?: string
}): Promise<RestaurantsPageResponse> {
  const response = await api.get<RestaurantsPageResponse>('/api/admin/restaurants', { params })
  return response.data
}

/**
 * Get detailed information about a specific restaurant
 */
export async function getRestaurant(id: number): Promise<RestaurantDetailDTO> {
  const response = await api.get<RestaurantDetailDTO>(`/api/admin/restaurants/${id}`)
  return response.data
}

/**
 * Update restaurant information
 */
export async function updateRestaurant(
  id: number,
  data: UpdateRestaurantDTO
): Promise<RestaurantDetailDTO> {
  const response = await api.put<RestaurantDetailDTO>(`/api/admin/restaurants/${id}`, data)
  return response.data
}

/**
 * Suspend a restaurant
 */
export async function suspendRestaurant(id: number): Promise<void> {
  await api.post(`/api/admin/restaurants/${id}/suspend`)
}

/**
 * Activate a suspended restaurant
 */
export async function activateRestaurant(id: number): Promise<void> {
  await api.post(`/api/admin/restaurants/${id}/activate`)
}

/**
 * Delete a restaurant permanently
 */
export async function deleteRestaurant(id: number): Promise<void> {
  await api.delete(`/api/admin/restaurants/${id}`)
}

/**
 * Change restaurant subscription plan
 */
export async function changeRestaurantPlan(
  restaurantId: number,
  data: ChangePlanRequestDTO
): Promise<void> {
  await api.post(`/api/admin/subscriptions/restaurant/${restaurantId}/change-plan`, data)
}
